/**
 * Provider-Agnostic OIDC Authentication Provider
 *
 * Wraps react-oidc-context AuthProvider with additional error handling,
 * structured logging, and configuration validation for OIDC authentication.
 *
 * Supports multiple identity providers (Cognito, Entra, etc.) through
 * provider-agnostic configuration from config/auth.ts.
 *
 * Usage:
 *   <OidcAuthProvider config={oidcConfig}>
 *     <App />
 *   </OidcAuthProvider>
 */

import React from 'react';
import { AuthProvider as ReactOidcAuthProvider } from 'react-oidc-context';
import type { AuthProviderProps as ReactOidcAuthProviderProps } from 'react-oidc-context';
import type { OidcConfig } from '../config/types';
import type { OidcProviderConfig } from './providers/types';
import {
  logAuthEvent,
  generateCorrelationId,
  storeCorrelationId,
  getStoredCorrelationId,
  clearCorrelationId
} from './logging';
import { getAuthProvider } from './providers/oidc';

interface OidcAuthProviderProps {
  children: React.ReactNode;
  config: OidcConfig | OidcProviderConfig;
}

/**
 * Convert OidcProviderConfig to react-oidc-context format if needed.
 */
function normalizeConfig(config: OidcConfig | OidcProviderConfig): OidcConfig {
  // Check if it's already in OidcConfig format (has client_id with underscore)
  if ('client_id' in config) {
    return config as OidcConfig;
  }

  // Convert from OidcProviderConfig format
  const providerConfig = config as OidcProviderConfig;
  return {
    authority: providerConfig.authority,
    client_id: providerConfig.clientId,
    redirect_uri: providerConfig.redirectUri,
    post_logout_redirect_uri: providerConfig.postLogoutRedirectUri,
    response_type: providerConfig.responseType as 'code',
    scope: providerConfig.scope,
    loadUserInfo: true,
    automaticSilentRenew: true,
  };
}

/**
 * Provider-agnostic OIDC authentication provider
 *
 * Provides authentication through react-oidc-context with:
 * - Authorization Code Flow with PKCE
 * - Automatic silent token renewal
 * - User info loading from identity provider
 * - Proper redirect handling after authentication
 * - Structured authentication event logging with correlation IDs
 * - Support for multiple identity providers (Cognito, Entra, etc.)
 */
export function OidcAuthProvider({
  children,
  config
}: OidcAuthProviderProps): JSX.Element {
  const normalizedConfig = normalizeConfig(config);
  const provider = getAuthProvider();
  // Generate correlation ID for this auth session
  React.useEffect(() => {
    if (!getStoredCorrelationId()) {
      const correlationId = generateCorrelationId();
      storeCorrelationId(correlationId);
      console.log(`[AUTH] Session correlation ID: ${correlationId}`);
    }
  }, []);

  // Handle redirect callback after authentication
  const onSigninCallback = (user: any) => {
    // Step 2 of Cognito sign-out: if we're returning from /logout → /authorize
    // round-trip, reject the auto-re-authentication. The Cognito session is now
    // cleared (step 1), so clearing local state and showing the sign-in page
    // means the next "Sign in" click will require real credentials.
    if (sessionStorage.getItem('cognito_logout_pending')) {
      sessionStorage.removeItem('cognito_logout_pending');

      logAuthEvent('logout_complete', 'Intercepted post-logout re-auth, clearing local state (step 2 of 2)', {
        correlationId: getStoredCorrelationId() || generateCorrelationId(),
        provider
      });

      localStorage.clear();
      sessionStorage.clear();
      document.cookie.split(';').forEach((c) => {
        const name = c.split('=')[0].trim();
        if (name) {
          document.cookie = `${name}=;expires=${new Date(0).toUTCString()};path=/`;
        }
      });
      window.location.replace(`${window.location.origin}?signed_out`);
      return;
    }

    const correlationId = getStoredCorrelationId() || generateCorrelationId();

    // Log successful authentication (provider-agnostic)
    logAuthEvent('auth_success', 'User successfully authenticated', {
      correlationId,
      provider,
      userId: user?.profile?.sub,
      userEmail: user?.profile?.email
    });

    // Remove OAuth query parameters from URL after successful authentication
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  // Handle sign out callback
  const onRemoveUser = () => {
    const correlationId = getStoredCorrelationId() || generateCorrelationId();

    logAuthEvent('sign_out', 'User signed out', {
      correlationId,
      provider
    });

    // Clear correlation ID after sign out
    clearCorrelationId();
  };

  // Convert our OidcConfig to react-oidc-context AuthProviderProps
  const authProviderProps: ReactOidcAuthProviderProps = {
    ...normalizedConfig,
    onSigninCallback,
    onRemoveUser
  };

  return (
    <ReactOidcAuthProvider {...authProviderProps}>
      {children}
    </ReactOidcAuthProvider>
  );
}

/**
 * Re-export the useAuth hook from react-oidc-context
 * This allows consumers to use a single auth module
 */
export { useAuth } from 'react-oidc-context';
